import { DOC_ACCEPT } from "./wizardConfig";

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentSlot = ({ slot, file, onFile, error, uploadState, onSelect, onClear }) => {
  const inputId = `doc-${slot.key}`;
  const uploading = uploadState?.status === "uploading";
  const uploaded = uploadState?.status === "done";
  const uploadError = uploadState?.status === "error" ? uploadState.error : null;
  const shownError = error || uploadError;
  return (
    <div
      className={[
        "rounded-xl border p-3 transition-colors",
        shownError ? "border-red-300 bg-red-50/40" : "border-slate-200 bg-slate-50/60",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 m-0">
            {slot.label}
            {slot.required ? <sup className="text-red-500"> *</sup> : null}
          </p>
          <p className="text-xs text-slate-500 m-0 mt-0.5">{slot.hint}</p>
        </div>
        {file ? (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-semibold text-slate-500 hover:text-rose-600 shrink-0"
          >
            Remove
          </button>
        ) : null}
      </div>

      {onFile && !file ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-4 text-sm font-medium text-emerald-800">
          Already on file with the office
        </div>
      ) : file ? (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-800 text-xs font-bold">
            {file.name.split(".").pop()?.toUpperCase()?.slice(0, 4) || "FILE"}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-900 m-0 truncate">{file.name}</p>
            <p className="text-[11px] text-slate-500 m-0">{formatBytes(file.size)}</p>
          </div>
          <label
            htmlFor={inputId}
            className="text-xs font-semibold text-teal-700 cursor-pointer hover:underline"
          >
            Replace
          </label>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-6 cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
        >
          <span className="text-sm font-semibold text-slate-800">Choose file</span>
          <span className="text-[11px] text-slate-500">PDF or image · max 10 MB</span>
        </label>
      )}

      <input
        id={inputId}
        type="file"
        className="sr-only"
        accept={DOC_ACCEPT}
        disabled={uploading || onFile}
        onChange={(event) => {
          const next = event.target.files?.[0] || null;
          event.target.value = "";
          onSelect(next);
        }}
      />
      {uploading ? (
        <p className="text-xs text-sky-600 m-0 mt-2">Uploading…</p>
      ) : uploaded ? (
        <p className="text-xs text-emerald-600 m-0 mt-2">Uploaded ✓</p>
      ) : null}
      {shownError ? <p className="text-xs text-red-500 m-0 mt-2">{shownError}</p> : null}
    </div>
  );
};

export const StepRail = ({ steps, stepIndex, onJump }) => (
  <ol className="flex flex-col gap-2 m-0 p-0 list-none">
    {steps.map((step, index) => {
      const done = index < stepIndex;
      const active = index === stepIndex;
      return (
        <li key={step.id}>
          <button
            type="button"
            onClick={() => onJump?.(index)}
            disabled={index > stepIndex}
            className={[
              "w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
              active
                ? "bg-white/15 text-white"
                : done
                  ? "text-white/90 hover:bg-white/10"
                  : "text-white/45 cursor-not-allowed",
            ].join(" ")}
          >
            <span
              className={[
                "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold border",
                active
                  ? "bg-white text-slate-900 border-white"
                  : done
                    ? "bg-emerald-400/20 text-emerald-200 border-emerald-300/40"
                    : "bg-transparent border-white/25 text-white/50",
              ].join(" ")}
            >
              {done ? "✓" : index + 1}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-tight">{step.title}</span>
              <span className="block text-[11px] opacity-80 leading-snug mt-0.5">{step.hint}</span>
            </span>
          </button>
        </li>
      );
    })}
  </ol>
);

export const MobileStepper = ({ steps, stepIndex }) => (
  <div className="lg:hidden mb-5">
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary m-0">
        Step {stepIndex + 1} of {steps.length}
      </p>
      <p className="text-xs text-app-text-muted m-0">{steps[stepIndex].title}</p>
    </div>
    <div className="flex gap-1.5">
      {steps.map((step, index) => (
        <div
          key={step.id}
          className={[
            "h-1.5 flex-1 rounded-full transition-colors",
            index <= stepIndex ? "bg-primary" : "bg-app-border",
          ].join(" ")}
        />
      ))}
    </div>
  </div>
);
